/**
 * OUW-336: días hábiles de México, para el margen del examen médico.
 *
 * Los feriados son los días de descanso obligatorio de la Ley Federal del
 * Trabajo (art. 74), calculados POR REGLA: no hay una lista por año que
 * mantener. Jueves y Viernes Santo, 2 de noviembre y 12 de diciembre NO son
 * oficiales por la LFT y quedan afuera (producto, OUW-336: «feriados oficiales
 * de México»). El día de elecciones también es descanso, pero siempre cae en
 * domingo, así que no cambia nada.
 *
 * Los días van y vienen como `YYYY-MM-DD` (el valor del `<input type="date">`)
 * y se operan en UTC: nada de la zona del dispositivo se cuela en la cuenta.
 */

/**
 * Días decretados fuera de la regla (`YYYY-MM-DD`), si algún año hay uno. Es lo
 * único que se tocaría a mano.
 */
const EXTRA_HOLIDAYS: ReadonlySet<string> = new Set<string>([]);

/** Año de transmisión del Poder Ejecutivo: 1 de octubre cada seis años desde 2024. */
const FIRST_INAUGURATION_YEAR = 2024;
const INAUGURATION_EVERY_YEARS = 6;

const MONDAY = 1;

function parseDay(day: string): { year: number; month: number; date: number } {
  const [year, month, date] = day.split('-').map(Number);
  return { year, month, date };
}

function utcDate(day: string): Date {
  const { year, month, date } = parseDay(day);
  return new Date(Date.UTC(year, month - 1, date));
}

function formatDay(date: Date): string {
  const month = `${date.getUTCMonth() + 1}`.padStart(2, '0');
  const day = `${date.getUTCDate()}`.padStart(2, '0');
  return `${date.getUTCFullYear()}-${month}-${day}`;
}

/** `true` si es el n-ésimo lunes de su mes (el 1.º cae entre el 1 y el 7, el 3.º entre el 15 y el 21). */
function isNthMonday(date: Date, nth: number): boolean {
  const dayOfMonth = date.getUTCDate();
  return date.getUTCDay() === MONDAY && dayOfMonth >= 7 * nth - 6 && dayOfMonth <= 7 * nth;
}

/** ¿Es descanso obligatorio en México (LFT art. 74)? */
export function isMxHoliday(day: string): boolean {
  if (EXTRA_HOLIDAYS.has(day)) return true;
  const date = utcDate(day);
  const month = date.getUTCMonth() + 1;
  const dayOfMonth = date.getUTCDate();
  const year = date.getUTCFullYear();

  if (month === 1 && dayOfMonth === 1) return true; // Año Nuevo
  if (month === 2 && isNthMonday(date, 1)) return true; // Constitución (5 de febrero)
  if (month === 3 && isNthMonday(date, 3)) return true; // Natalicio de Benito Juárez (21 de marzo)
  if (month === 5 && dayOfMonth === 1) return true; // Día del Trabajo
  if (month === 9 && dayOfMonth === 16) return true; // Independencia
  if (
    month === 10 &&
    dayOfMonth === 1 &&
    year >= FIRST_INAUGURATION_YEAR &&
    (year - FIRST_INAUGURATION_YEAR) % INAUGURATION_EVERY_YEARS === 0
  ) {
    return true; // Transmisión del Poder Ejecutivo Federal
  }
  if (month === 11 && isNthMonday(date, 3)) return true; // Revolución (20 de noviembre)
  if (month === 12 && dayOfMonth === 25) return true; // Navidad
  return false;
}

/** Lunes a viernes y no feriado. */
export function isMxBusinessDay(day: string): boolean {
  const weekday = utcDate(day).getUTCDay();
  return weekday !== 0 && weekday !== 6 && !isMxHoliday(day);
}

/**
 * Sábado o domingo. El día es `YYYY-MM-DD` y el weekday se lee en UTC, igual
 * que el resto de este archivo: no depende de la zona del dispositivo.
 *
 * Un feriado que cae entre semana NO es fin de semana. El calendario de citas
 * cierra el sábado y el domingo, y deja el feriado elegible.
 */
export function isWeekendDay(day: string): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(day)) return false;
  const weekday = utcDate(day).getUTCDay();
  return weekday === 0 || weekday === 6;
}

/** `day` + `days` días corridos. */
export function addCalendarDays(day: string, days: number): string {
  const { year, month, date } = parseDay(day);
  return formatDay(new Date(Date.UTC(year, month - 1, date + days)));
}

/**
 * El `count`-ésimo día hábil DESPUÉS de `day` (el propio `day` no cuenta):
 * martes 6-oct-2026 + 4 → lunes 12-oct-2026.
 */
export function addMxBusinessDays(day: string, count: number): string {
  let current = day;
  let remaining = count;
  while (remaining > 0) {
    current = addCalendarDays(current, 1);
    if (isMxBusinessDay(current)) remaining -= 1;
  }
  return current;
}
